import { TextFieldProps, useTheme } from '@mui/material';
import {
  DateRangePicker,
  type DateRangePickerProps,
  pickersLayoutClasses,
} from '@mui/x-date-pickers-pro';
import { forwardRef, useState } from 'react';

import {
  dateTimeToDate,
  dateTimeToDateRange,
  dateToDateTime,
  dateToDateTimeRange,
} from 'helpers/datetime';

import ActionBar from './ActionBar';
import DateRangeField from './DateRangeField';

import type { DateTime } from 'luxon';

type OmittedDateRangePickerProps =
  | 'closeOnSelect'
  | 'components'
  | 'componentsProps'
  | 'desktopModeMediaQuery'
  | 'localeText'
  | 'onAccept'
  | 'onChange'
  | 'slotProps'
  | 'slots'
  | 'value'
  | 'viewRenderers';

export type LocatedDateRangerPickerValue = {
  end: Date | null;
  start: Date | null;
};

interface LocatedDateRangerPickerProps extends Omit<
  DateRangePickerProps<Date>,
  OmittedDateRangePickerProps
> {
  allowClear?: boolean;
  onChange?: (date: LocatedDateRangerPickerValue) => void;
  placeholder?: string;
  showCurrentTimeZone?: boolean;
  TextFieldProps?: TextFieldProps;
  timezone?: string;
  value: LocatedDateRangerPickerValue;
}

const LocatedDateRangerPicker = forwardRef<
  HTMLDivElement,
  LocatedDateRangerPickerProps
>(
  (
    {
      allowClear,
      onChange,
      placeholder,
      showCurrentTimeZone = true,
      TextFieldProps,
      timezone,
      value,
      ...props
    }: LocatedDateRangerPickerProps,
    ref,
  ) => {
    const theme = useTheme();

    const [open, setOpen] = useState(false);
    const [internalValue, setInternalValue] =
      useState<LocatedDateRangerPickerValue>(value);

    const onClear = () => {
      setInternalValue({ end: null, start: null });
      onChange?.({ end: null, start: null });
    };

    /**
     * Convert between Date and DateTime props due to the usage of Luxon DateTime in the
     * MUI DateRangePicker component
     */
    const convertedDateRangePickerProps: Omit<
      DateRangePickerProps<DateTime>,
      OmittedDateRangePickerProps
    > = {
      ...props,
      calendars: props.calendars ?? 1,
      dayOfWeekFormatter:
        props.dayOfWeekFormatter &&
        ((day, date) => props.dayOfWeekFormatter!(day, dateTimeToDate(date))),
      defaultCalendarMonth:
        props.defaultCalendarMonth &&
        dateToDateTime(props.defaultCalendarMonth, { timezone }),
      defaultValue:
        props.defaultValue && dateToDateTimeRange(props.defaultValue),
      maxDate: props.maxDate && dateToDateTime(props.maxDate, { timezone }),
      minDate: props.minDate && dateToDateTime(props.minDate, { timezone }),
      onError:
        props.onError &&
        ((error, value) => props.onError!(error, dateTimeToDateRange(value))),
      onMonthChange:
        props.onMonthChange &&
        ((month) => props.onMonthChange!(dateTimeToDate(month))),
      referenceDate:
        props.referenceDate &&
        dateToDateTime(props.referenceDate, { timezone }),
      shouldDisableDate:
        props.shouldDisableDate &&
        ((day, position) =>
          props.shouldDisableDate!(dateTimeToDate(day), position)),
      timezone,
    };

    return (
      <DateRangePicker<DateTime>
        {...convertedDateRangePickerProps}
        closeOnSelect={false}
        desktopModeMediaQuery={`@media (min-width: ${theme.breakpoints.values.sm}px)`}
        // See ActionBar for why we don't allow passing onAccept as a prop in this component.
        onAccept={undefined}
        onChange={([start, end]) => {
          setInternalValue({
            end: end ? dateTimeToDate(end) : null,
            start: start ? dateTimeToDate(start) : null,
          });
        }}
        onClose={() => {
          setOpen(false);
          props.onClose?.();
        }}
        onOpen={() => {
          setOpen(true);
          setInternalValue(value);
        }}
        ref={ref}
        // Type cast slotProps and slots to work around MUI X typing issue.
        // See: https://github.com/mui/mui-x/issues/9775
        slotProps={{
          actionBar: {
            internalValue,
            onChange,
            setInternalValue,
          } as any,
          field: {
            allowClear,
            onClear,
            placeholder,
            showCurrentTimeZone,
            TextFieldProps,
            timezoneProp: timezone,
          } as any,
          layout: {
            sx: {
              [`.${pickersLayoutClasses.actionBar}`]: {
                gridRow: 3,
              },
            },
          },
        }}
        slots={{
          actionBar: ActionBar as any,
          field: DateRangeField,
        }}
        value={
          open
            ? [
                internalValue.start
                  ? dateToDateTime(internalValue.start, { timezone })
                  : null,
                internalValue.end
                  ? dateToDateTime(internalValue.end, { timezone })
                  : null,
              ]
            : [
                value.start ? dateToDateTime(value.start, { timezone }) : null,
                value.end ? dateToDateTime(value.end, { timezone }) : null,
              ]
        }
      />
    );
  },
);

export default LocatedDateRangerPicker;
